import React from "react";
import s from "./RadioGroup.module.scss";
import {FontIcon} from "../FontIcon";

export interface IRadioGroupOption<T extends string = string> {
  /** The value associated with this option */
  value: T;
  /** Display label for the option - can be a string or a React component (e.g., <FontIcon />) */
  label: string | React.ReactNode;
}

export interface IRadioGroupProps<T extends string = string> {
  /** Array of options to display */
  options: IRadioGroupOption<T>[];
  /** The currently selected value */
  value: T;
  /** Callback function called when a new option is selected */
  onChange?: (value: T) => void;
  /** Optional label for the group */
  label?: string;
  /** Additional CSS class name for custom styling */
  additionalClassName?: string;
  /** Visual theme - defaults to "radios" */
  type?: 'radios' | 'buttons' | 'checkmarks';
}

export default function RadioGroup<T extends string = string>({
  options,
  value,
  onChange,
  label,
  additionalClassName = '',
  type = 'radios'
}: IRadioGroupProps<T>) {
  const calculateOrientation = () => {
    switch (type) {
      case 'radios':
        return 'vertical';
      case 'buttons':
      case 'checkmarks':
        return 'horizontal';
      default:
        return 'vertical';
    }
  }

  const orientation = calculateOrientation();

  const handleChange = (newValue: T) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  const renderLabel = () => {
    if (!label) return null;
    return <div className={s.groupLabel}>{label}</div>;
  };

  const renderOption = (option: IRadioGroupOption<T>, index: number) => {
    const isSelected = value === option.value;
    const optionId = `radio-group-option-${index}-${option.value}`;

    if (type === 'radios') {
      return (
        <li key={optionId} className={s.radioOption}>
          <label>
            <input
              type="radio"
              value={option.value}
              checked={isSelected}
              onChange={() => handleChange(option.value)}
            />
            <span className={s.radioLabel}>{option.label}</span>
          </label>
        </li>
      );
    }

    if (type === 'checkmarks') {
      return (
        <div
          key={optionId}
          role="button"
          tabIndex={0}
          className={`${s.checkmarkOption} ${isSelected ? s.selected : ''}`}
          onClick={() => handleChange(option.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleChange(option.value);
            }
          }}
        >
          <span className={s.checkmarkLabel}>{option.label}</span>
          <span className={s.checkmark}>
            {isSelected && <FontIcon icon={"icon-check"} />}
          </span>
        </div>
      );
    }

    if (type === 'buttons') {
      return (
        <div
          key={optionId}
          role="button"
          tabIndex={0}
          className={`${s.buttonOption} ${isSelected ? s.selected : ''}`}
          aria-current={isSelected}
          onClick={() => handleChange(option.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleChange(option.value);
            }
          }}
        >
          {option.label}
        </div>
      );
    }

    return null;
  };

  const containerClassName = `${s.radioGroup} ${s[orientation]} ${s[type]} ${additionalClassName}`;

  return (
    <div className={containerClassName}>
      {renderLabel()}
      <div className={s.options} role="group">
        {type === 'radios' ? (
          <ol className={s.radioList}>
            {options.map((option, index) => renderOption(option, index))}
          </ol>
        ) : (
          options.map((option, index) => renderOption(option, index))
        )}
      </div>
    </div>
  );
}

